import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCampaign } from '../contexts/CampaignContext';
import { useMap } from '../contexts/MapContext';
import MapCanvas from '../components/canvas/MapCanvas';
import PlayerPresence from '../components/dm/PlayerPresence';
import { SCENE_MAP, randomTrack, trackName } from '../lib/ambience';
import DiceRollOverlay from '../components/dice/DiceRollOverlay';
import SecretDoorSoundWatcher from '../components/ui/SecretDoorSoundWatcher';
import { setMusicAudio, clearMusicAudio, updateBaseVolume } from '../lib/audioDuck';

// ── Helpers ───────────────────────────────────────────────────────────────

function hpConditionLabel(hp, maxHp) {
  if (hp <= 0)           return { label: 'Down',     colour: '#374151' };
  const pct = hp / maxHp;
  if (pct > 0.5)         return { label: 'Healthy',  colour: '#4ade80' };
  if (pct > 0.35)        return { label: 'Wounded',  colour: '#fb923c' };
  if (pct > 0.15)        return { label: 'Bloodied', colour: '#facc15' };
  return                        { label: 'Critical', colour: '#f87171' };
}

function HPBar({ hp, maxHp, type }) {
  const showFull = type === 'player' || type === 'friendlyNPC';
  const pct      = Math.max(0, hp / maxHp);
  const { label, colour } = hpConditionLabel(hp, maxHp);
  if (!showFull) return <span style={{ color: colour, fontSize: 14, fontWeight: 700 }}>{label}</span>;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={styles.hpBarOuter}>
        <div style={{ ...styles.hpBarFill, width: `${pct * 100}%`, background: colour }} />
      </div>
      <span style={{ color: colour, fontSize: 14, fontWeight: 700, minWidth: 28 }}>{hp}</span>
    </div>
  );
}

// ── Turn timer display ────────────────────────────────────────────────────
function TurnTimer({ timer }) {
  const { enabled = false, seconds = 60, startedAt = null, running = false, remaining: storedRemaining = seconds } = timer || {};
  const [remaining, setRemaining] = useState(storedRemaining);
  const flashed = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (!running || !startedAt) {
      setRemaining(storedRemaining);
      flashed.current = false;
      return;
    }
    const tick = () => {
      const elapsed = (Date.now() - startedAt) / 1000;
      const r = Math.max(0, seconds - elapsed);
      setRemaining(r);
      if (r <= 10 && r > 0 && !flashed.current) flashed.current = true;
      if (r > 10) flashed.current = false;
    };
    tick();
    const id = setInterval(tick, 200);
    return () => clearInterval(id);
  }, [enabled, running, startedAt, seconds, storedRemaining]);

  if (!enabled) return null;

  const pct = seconds > 0 ? Math.max(0, Math.min(1, remaining / seconds)) : 0;
  const lowTime = remaining <= 10;
  const expired = remaining <= 0;
  const barColour = expired ? '#c0392b' : lowTime ? '#f87171' : pct > 0.5 ? '#4ade80' : pct > 0.25 ? '#facc15' : '#fb923c';

  const mm = Math.floor(remaining / 60);
  const ss = Math.floor(remaining % 60);
  const label = `${mm}:${ss.toString().padStart(2, '0')}`;

  return (
    <div style={{
      position: 'absolute', top: 96, left: '50%', transform: 'translateX(-50%)',
      background: 'rgba(10,15,30,0.92)', backdropFilter: 'blur(12px)',
      border: `2px solid ${lowTime ? '#c0392b' : '#8b7340'}`, borderRadius: 6,
      padding: '10px 24px', zIndex: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.7)',
      minWidth: 280, pointerEvents: 'none',
      animation: lowTime && !expired ? 'turnPulse 0.6s ease-out infinite' : 'none',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 6 }}>
        <span style={{ fontSize: 12, color: '#9a8b6e', fontWeight: 700, fontFamily: "'Press Start 2P', monospace", letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          ⏱ Turn Timer
        </span>
        <span style={{ fontSize: 22, color: barColour, fontWeight: 900, fontFamily: "'Press Start 2P', monospace", fontVariantNumeric: 'tabular-nums' }}>
          {expired ? "TIME'S UP" : label}
        </span>
      </div>
      <div style={{ height: 8, background: '#0e1528', borderRadius: 4, overflow: 'hidden', border: '1px solid #4a3d28' }}>
        <div style={{ height: '100%', width: `${pct * 100}%`, background: barColour, transition: 'width 0.2s linear, background 0.3s' }} />
      </div>
    </div>
  );
}

// ── Compute bounding box of all revealed cells ────────────────────────────
function useRevealedBounds(fog, gridW, gridH) {
  return useMemo(() => {
    let minX = gridW, minY = gridH, maxX = -1, maxY = -1;
    for (let y = 0; y < gridH; y++) {
      for (let x = 0; x < gridW; x++) {
        const idx = y * gridW + x;
        if (fog[idx]) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0) return null; // nothing revealed
    return { minX, minY, maxX, maxY };
  }, [fog, gridW, gridH]);
}

// ── Main component ────────────────────────────────────────────────────────

export default function DisplayView() {
  const { tokens, initiative, musicState, saveMusicState } = useCampaign();
  const { fog, gridW, gridH } = useMap();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const handleLogout = () => { logout(); navigate(`/session/${id}/dm/login`); };

  const { turnOrder, activeIndex, round } = initiative;
  const inCombat      = initiative.phase === 'active' && turnOrder.length > 0;
  const activeTokenId = turnOrder[activeIndex] ?? null;
  const activeToken   = activeTokenId ? tokens[activeTokenId] : null;
  const battleScreen     = initiative.battleScreen ?? false;
  const combatEndScreen  = initiative.combatEndScreen ?? false;

  const revealedBounds = useRevealedBounds(fog, gridW, gridH);

  // ── Fullscreen ───────────────────────────────────────────────────────────
  const containerRef  = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    if (!document.fullscreenElement) {
      await containerRef.current?.requestFullscreen?.();
    } else {
      await document.exitFullscreen?.();
    }
  }, []);

  // ── Ambient music player ─────────────────────────────────────────────────
  const audioRef = useRef(null);
  const musicStateRef = useRef(musicState);
  useEffect(() => { musicStateRef.current = musicState; }, [musicState]);

  useEffect(() => {
    const { playing, trackUrl, volume, loopMode = 'playlist', scene } = musicState || {};

    if (!playing || !trackUrl) {
      if (audioRef.current) {
        clearMusicAudio(audioRef.current);
        audioRef.current.pause();
        audioRef.current = null;
      }
      return;
    }

    const shouldLoop = loopMode === 'track';

    // If track changed, create new Audio
    if (!audioRef.current || audioRef.current.src !== new URL(trackUrl, window.location.origin).href) {
      if (audioRef.current) audioRef.current.pause();
      const audio = new Audio(trackUrl);
      audio.onerror = () => { audio.pause(); audio.src = ''; };
      audio.loop = shouldLoop;
      audio.volume = volume ?? 0.5;

      audio.onended = () => {
        const ms = musicStateRef.current;
        if (!ms) return;
        if (ms.loopMode === 'playlist' && ms.scene) {
          const s = SCENE_MAP[ms.scene];
          if (s && s.tracks.length > 0) {
            const nextIdx = ((ms.playlistIndex ?? 0) + 1) % s.tracks.length;
            saveMusicState({ ...ms, trackUrl: s.tracks[nextIdx], playlistIndex: nextIdx });
          }
        } else if (ms.loopMode === 'none') {
          saveMusicState({ ...ms, playing: false });
        }
      };

      audio.play().catch(() => {});
      audioRef.current = audio;
      setMusicAudio(audio, volume ?? 0.5);
    } else {
      // Same track — just update volume/loop
      audioRef.current.volume = volume ?? 0.5;
      audioRef.current.loop = shouldLoop;
      updateBaseVolume(volume ?? 0.5);
    }

    return () => {
      if (audioRef.current) {
        clearMusicAudio(audioRef.current);
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [musicState?.playing, musicState?.trackUrl, musicState?.volume, musicState?.loopMode, saveMusicState]);

  // ── Battle start screen ──────────────────────────────────────────────────
  if (battleScreen) {
    return (
      <div style={{ width: '100%', height: '100%', background: '#080510', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 120, marginBottom: 32 }}>⚔</div>
          <div style={{ fontSize: 72, fontWeight: 900, fontFamily: "'Press Start 2P', monospace", color: '#c0392b', letterSpacing: '0.12em', textShadow: '0 0 60px rgba(192,57,43,0.9), 0 0 120px rgba(192,57,43,0.5)', marginBottom: 20 }}>
            BATTLE BEGINS
          </div>
          <div style={{ fontSize: 28, color: '#9a8b6e', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
            Round 1
          </div>
        </div>
      </div>
    );
  }

  // ── Combat end screen ────────────────────────────────────────────────────
  if (combatEndScreen) {
    const isDefeat = (initiative.combatOutcome ?? 'victory') === 'defeat';
    return (
      <div style={{ width: '100%', height: '100%', background: '#080510', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 120, marginBottom: 32 }}>{isDefeat ? '💀' : '🛡'}</div>
          <div style={{ fontSize: 72, fontWeight: 900, color: isDefeat ? '#c0392b' : '#4ade80', letterSpacing: '0.12em', textShadow: `0 0 60px ${isDefeat ? 'rgba(192,57,43,0.9)' : 'rgba(74,222,128,0.9)'}, 0 0 120px ${isDefeat ? 'rgba(192,57,43,0.5)' : 'rgba(74,222,128,0.5)'}`, marginBottom: 20 }}>
            {isDefeat ? 'DEFEAT' : 'VICTORY'}
          </div>
          <div style={{ fontSize: 28, color: '#9a8b6e', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
            {isDefeat ? 'The party has fallen...' : 'The battle is won!'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={styles.layout}>
      {/* Map */}
      <div style={styles.mapArea}>
        <MapCanvas isDm={false} readOnly={true} autoFitBounds={revealedBounds} />
      </div>

      {/* Dice broadcast — large + with sound on the TV */}
      <DiceRollOverlay withSound large />

      {/* Zelda chime when a secret door reveals — TV only. */}
      <SecretDoorSoundWatcher />


      {/* Player presence + music indicator — top right */}
      <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 5, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
        <PlayerPresence compact />
        {musicState?.playing && musicState?.scene && SCENE_MAP[musicState.scene] && (
          <div style={{ background: 'rgba(10,15,30,0.85)', border: '1px solid #4a3d28', borderRadius: 4, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#f0e6d0' }}>
            <span>{SCENE_MAP[musicState.scene].icon}</span>
            <span>♫ {SCENE_MAP[musicState.scene].label}</span>
          </div>
        )}
      </div>

      {/* Bottom left controls */}
      <div style={styles.bottomControls}>
        <button style={styles.fsBtn} onClick={toggleFullscreen} title={isFullscreen ? 'Exit fullscreen' : 'Go fullscreen'}>
          {isFullscreen ? '⛶' : '⛶'}
          <span style={styles.fsBtnLabel}>{isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}</span>
        </button>
        <button style={styles.logoutBtn} onClick={handleLogout} title="Log out">
          ← Exit
        </button>
      </div>

      {/* Active turn banner */}
      {inCombat && activeToken && (() => {
        const speed = activeToken.speed || 30;
        const speedCells = Math.floor(speed / 5) * (activeToken.dashed ? 2 : 1);
        const used = activeToken.movementUsed || 0;
        const usedFt = used * 5;
        const totalFt = speedCells * 5;
        return (
          <div key={activeTokenId} style={{ ...styles.activeBanner, borderColor: activeToken.colour || '#d4a847' }}>
            <span style={{ color: activeToken.colour || '#d4a847', fontWeight: 900, fontSize: 24, animation: 'turnPulse 1s ease-out' }}>
              ▶ {activeToken.name}
            </span>
            <span style={styles.movementDisplay}>{usedFt}/{totalFt} ft{activeToken.dashed ? ' (Dash)' : ''}</span>
            <span style={styles.roundCounter}>Round {round}</span>
          </div>
        );
      })()}

      {/* Turn timer */}
      {initiative.turnTimer?.enabled && <TurnTimer timer={initiative.turnTimer} />}

      {/* Initiative sidebar */}
      {inCombat && (
        <div style={styles.initiativeSidebar}>
          <div style={styles.sidebarHeader}>⚔ Initiative</div>
          <ol style={styles.list}>
            {turnOrder.map((tokenId, idx) => {
              const t      = tokens[tokenId];
              const active = idx === activeIndex;
              if (!t) return null;
              return (
                <li key={tokenId} style={{ ...styles.entry, ...(active ? styles.activeEntry : {}) }}>
                  <span style={{ ...styles.dot, background: t.colour || '#888' }} />
                  <div style={styles.entryBody}>
                    <span style={styles.entryName}>{t.name}</span>
                    <HPBar hp={t.hp} maxHp={t.maxHp} type={t.type} />
                    {/* Conditions */}
                    {t.conditions?.length > 0 && (
                      <div style={styles.statusList}>
                        {t.conditions.map((c) => (
                          <span key={c} style={{ ...styles.statusWord, color: '#f87171' }}>
                            <span style={{ ...styles.statusDot, background: '#f87171' }} />{c}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Buffs */}
                    {t.buffs?.length > 0 && (
                      <div style={styles.statusList}>
                        {t.buffs.map((b, i) => (
                          <span key={i} style={{ ...styles.statusWord, color: b.color || '#d4a847' }}>
                            <span style={{ ...styles.statusDot, background: b.color || '#d4a847' }} />
                            {b.label}{!b.permanent ? ` (${b.duration}r)` : ''}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {active  && <span style={{ color: '#d4a847', fontSize: 14 }}>▶</span>}
                  {t.defeated && <span style={{ color: '#e07070', fontSize: 14 }}>☠</span>}
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {/* No fog revealed message */}
      {!revealedBounds && (
        <div style={styles.noReveal}>
          <span style={styles.noRevealText}>Waiting for DM to reveal the map…</span>
        </div>
      )}
    </div>
  );
}

const styles = {
  layout:            { width: '100%', height: '100%', position: 'relative', overflow: 'hidden', background: '#0a0f1e' },
  mapArea:           { position: 'absolute', inset: 0 },

  bottomControls:    { position: 'absolute', bottom: 16, left: 16, zIndex: 20, display: 'flex', gap: 8, alignItems: 'center' },
  fsBtn:             { background: 'rgba(20,32,56,0.9)', border: '1px solid #8b7340', borderRadius: 4, color: '#9a8b6e', padding: '7px 14px', cursor: 'pointer', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', gap: 7, fontSize: 16, transition: 'color 0.15s' },
  fsBtnLabel:        { fontSize: 12, fontWeight: 600, fontFamily: "'Press Start 2P', monospace" },
  logoutBtn:         { background: 'rgba(20,32,56,0.9)', border: '1px solid #8b7340', borderRadius: 4, color: '#9a8b6e', padding: '7px 14px', cursor: 'pointer', backdropFilter: 'blur(8px)', fontSize: 12, fontWeight: 600, fontFamily: "'Press Start 2P', monospace", transition: 'color 0.15s' },

  noReveal:          { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' },
  noRevealText:      { color: '#4a3d28', fontSize: 18, fontWeight: 600, fontFamily: "'Press Start 2P', monospace", letterSpacing: '0.06em' },

  activeBanner:      { position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)', background: 'rgba(10,15,30,0.92)', backdropFilter: 'blur(12px)', border: '2px solid #8b7340', borderRadius: 6, padding: '14px 36px', display: 'flex', alignItems: 'center', gap: 24, zIndex: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.7)', pointerEvents: 'none' },
  roundCounter:      { fontSize: 18, fontFamily: "'Press Start 2P', monospace", color: '#9a8b6e', fontWeight: 600 },
  movementDisplay:   { fontSize: 14, color: '#4ade80', fontWeight: 700, fontFamily: "'Press Start 2P', monospace" },

  initiativeSidebar: { position: 'absolute', top: 16, right: 16, bottom: 16, width: 320, background: 'rgba(20,32,56,0.94)', backdropFilter: 'blur(12px)', border: '2px solid #8b7340', borderRadius: 6, display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.6)', pointerEvents: 'none' },
  sidebarHeader:     { padding: '10px 14px', borderBottom: '2px solid #4a3d28', fontWeight: 800, fontFamily: "'Press Start 2P', monospace", fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#d4a847' },
  list:              { listStyle: 'none', overflowY: 'auto', flex: 1, padding: '6px 0' },
  entry:             { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px', fontSize: 14 },
  activeEntry:       { background: 'rgba(212,168,71,0.2)', borderLeft: '4px solid #d4a847', boxShadow: 'inset 0 0 20px rgba(212,168,71,0.1)' },
  dot:               { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 },
  entryBody:         { flex: 1, display: 'flex', flexDirection: 'column', gap: 3, overflow: 'hidden' },
  entryName:         { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600, fontFamily: "'Press Start 2P', monospace", fontSize: 16 },
  statusList:        { display: 'flex', flexWrap: 'wrap', gap: '3px 8px', marginTop: 3 },
  statusWord:        { fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4, textTransform: 'capitalize', letterSpacing: '0.03em' },
  statusDot:         { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  hpBarOuter:        { height: 6, background: '#0e1528', borderRadius: 3, overflow: 'hidden', flex: 1, border: '1px solid #4a3d28' },
  hpBarFill:         { height: '100%', borderRadius: 2, transition: 'width 0.4s ease' },
};
